import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Facebook, Instagram, Youtube, Mail, ShieldCheck, Lock, IndianRupee, Award, Users, Briefcase, CheckCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';
import LanguageSwitcher from './LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOCIAL_ICONS = {
  linkedin: Linkedin,
  twitter: Twitter,
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
};

const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stats, setStats] = useState(null);
  const [socialLinks, setSocialLinks] = useState([]);

  // Live trust counts — cached server-side for 30 min
  useEffect(() => {
    axios.get(`${API}/public/stats`).then((r) => setStats(r.data)).catch(() => {});
    // Phase 58 — admin-set social URLs. Empty array if not configured.
    axios.get(`${API}/social-kit/links`)
      .then((r) => setSocialLinks(r.data?.items || []))
      .catch(() => setSocialLinks([]));
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setIsSubmitting(false);
    }
  };

  const footerSocials = (socialLinks || []).filter((s) => s.url && s.show_in_footer !== false);
  const jsonLdSameAs = (socialLinks || [])
    .filter((s) => s.url && s.show_in_jsonld !== false)
    .map((s) => s.url);

  return (
    <footer className="bg-slate-950 border-t border-white/5">
      {jsonLdSameAs.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'IB Club India',
            url: 'https://ibclub.org',
            logo: 'https://ibclub.org/logo512.png',
            sameAs: jsonLdSameAs,
          })}</script>
        </Helmet>
      )}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        {/* Massive IB Club text */}
        <div className="relative mb-16">
          <h2 className="text-9xl font-playfair font-bold text-slate-900 opacity-5 select-none">
            IB CLUB
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* About */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 font-playfair">About IB Club</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              India's premium community connecting investors, entrepreneurs, and business leaders.
            </p>
            <div className="flex space-x-4 mt-6" data-testid="footer-socials">
              {footerSocials.length === 0 ? (
                <span className="text-[10px] text-slate-600 uppercase tracking-wider italic">
                  Launching soon
                </span>
              ) : (
                footerSocials.map((s) => {
                  const Icon = SOCIAL_ICONS[s.icon] || Linkedin;
                  return (
                    <a
                      key={s.platform}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`footer-social-${s.platform}`}
                      aria-label={s.label || s.platform}
                      className="text-slate-400 hover:text-[#d4af37] transition-colors"
                    >
                      <Icon size={20} />
                    </a>
                  );
                })
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 font-playfair">Explore</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/about" className="text-slate-400 hover:text-[#d4af37] transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/membership" className="text-slate-400 hover:text-[#d4af37] transition-colors">
                  Membership
                </Link>
              </li>
              <li>
                <Link to="/investments" className="text-slate-400 hover:text-[#d4af37] transition-colors">
                  Investment Board
                </Link>
              </li>
              <li>
                <Link to="/business-directory" className="text-slate-400 hover:text-[#d4af37] transition-colors">
                  Business Directory
                </Link>
              </li>
              <li>
                <Link to="/city-chapters" className="text-slate-400 hover:text-[#d4af37] transition-colors">
                  City Chapters
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 font-playfair">Resources</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/faq" className="text-slate-400 hover:text-[#d4af37] transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/downloads" className="text-slate-400 hover:text-[#d4af37] transition-colors">
                  Downloads
                </Link>
              </li>
              <li>
                <Link to="/sponsorship" className="text-slate-400 hover:text-[#d4af37] transition-colors">
                  Sponsorship
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-400 hover:text-[#d4af37] transition-colors">
                  Contact Us
                </Link>
              </li>
              <li>
                <a
                  href={`${BACKEND_URL}/api/downloads/brochure`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-brochure-link"
                  className="inline-flex items-center gap-1.5 text-slate-400 hover:text-[#d4af37] transition-colors"
                >
                  Member Brochure (PDF)
                  <span className="text-[10px] uppercase tracking-wider text-[#d4af37]">↓</span>
                </a>
              </li>
              <li>
                <Link
                  to="/badges"
                  data-testid="footer-badges-link"
                  className="text-slate-400 hover:text-[#d4af37] transition-colors"
                >
                  Badges & Achievements
                </Link>
              </li>
              <li>
                <Link
                  to="/how-it-works"
                  data-testid="footer-how-it-works-link"
                  className="text-slate-400 hover:text-[#d4af37] transition-colors"
                >
                  How It Works
                </Link>
              </li>
              <li>
                <Link
                  to="/help"
                  data-testid="footer-help-link"
                  className="text-slate-400 hover:text-[#d4af37] transition-colors"
                >
                  Help Center
                </Link>
              </li>
              <li>
                <Link
                  to="/press"
                  data-testid="footer-press-link"
                  className="text-slate-400 hover:text-[#d4af37] transition-colors"
                >
                  Press Kit
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 font-playfair">Newsletter</h3>
            <p className="text-sm text-slate-400 mb-3">
              Subscribe for exclusive updates and opportunities.
            </p>
            <p className="text-xs text-[#d4af37] mb-4 font-medium" data-testid="footer-trial-line">
              ✨ 3-day free trial · No credit card needed
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                required
                data-testid="newsletter-email-input"
                className="flex-1 bg-slate-900 border border-white/10 focus:border-[#d4af37] text-white text-sm px-4 py-2 outline-none transition-colors"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="newsletter-submit-button"
                className="bg-[#d4af37] text-slate-950 hover:bg-[#e5c558] px-4 py-2 transition-colors disabled:opacity-50"
              >
                <Mail size={18} />
              </button>
            </form>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-white/5 text-center text-sm text-slate-500">
          {/* Live trust panel — DB-backed (Feb 2026) */}
          {stats && (
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 max-w-3xl mx-auto"
              data-testid="footer-live-stats"
            >
              <LiveStat icon={Users} label="Members" value={stats.members_display} testId="stat-members" />
              <LiveStat icon={ShieldCheck} label="Verified investors" value={stats.verified_investors_display} testId="stat-investors" />
              <LiveStat icon={Briefcase} label="Listed businesses" value={stats.active_businesses_display} testId="stat-businesses" />
              <LiveStat icon={CheckCircle} label="Payouts processed" value={stats.completed_payouts_display} testId="stat-payouts" />
            </div>
          )}

          {/* Trust badges — relocated from Home (Feb 2026) */}
          <div
            className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-5 text-xs uppercase tracking-wider text-slate-400"
            data-testid="footer-trust-signals"
          >
            <span className="inline-flex items-center gap-1.5" data-testid="trust-dpdpa">
              <ShieldCheck size={13} className="text-emerald-400" />
              DPDPA 2023 Compliant
            </span>
            <span className="hidden md:inline text-slate-700">·</span>
            <span className="inline-flex items-center gap-1.5" data-testid="trust-razorpay">
              <IndianRupee size={13} className="text-[#d4af37]" />
              Razorpay Secured Payments
            </span>
            <span className="hidden md:inline text-slate-700">·</span>
            <span className="inline-flex items-center gap-1.5" data-testid="trust-tls">
              <Lock size={13} className="text-blue-300" />
              TLS 1.3 + AES-256 at Rest
            </span>
            <span className="hidden md:inline text-slate-700">·</span>
            <span className="inline-flex items-center gap-1.5" data-testid="trust-curated">
              <Award size={13} className="text-purple-300" />
              Curated Indian Membership
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mb-3">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-[#d4af37] transition-colors">
              Privacy Policy
            </Link>
            <span className="text-slate-700">·</span>
            <Link to="/privacy-rights" data-testid="footer-rights-link" className="hover:text-[#d4af37] transition-colors">
              Your Privacy Rights
            </Link>
            <span className="text-slate-700">·</span>
            <Link to="/terms-of-use" data-testid="footer-terms-link" className="hover:text-[#d4af37] transition-colors">
              Terms of Use
            </Link>
            <span className="text-slate-700">·</span>
            <Link to="/trust" data-testid="footer-trust-link" className="hover:text-[#d4af37] transition-colors">
              Trust Center
            </Link>
            <span className="text-slate-700">·</span>
            <Link to="/contact" className="hover:text-[#d4af37] transition-colors">
              Contact
            </Link>
          </div>
          <p>&copy; 2026 IB Club India. {t('footer.rights')} | Empowering India's Investors &amp; Business Leaders</p>
          <div className="mt-4 flex justify-center">
            <LanguageSwitcher variant="text" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

const LiveStat = ({ icon: Icon, label, value, testId }) => (
  <div className="text-center" data-testid={testId}>
    <Icon className="text-[#d4af37] mx-auto mb-1.5" size={18} />
    <p className="text-white font-playfair text-xl md:text-2xl font-semibold leading-tight">{value}</p>
    <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-0.5">{label}</p>
  </div>
);